import { useQuery } from '@tanstack/react-query';
import { api, ApiError, MissingModelError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * `agent-ux-optimization` Phase 6 Session 6 (2026-05-08).
 *
 * `useConversationAccess(conversationId)` — tri-state probe of whether
 * the currently-signed-in user can access a conversation through the
 * authenticated API surface (`GET /api/conversations/:id`).
 *
 * Maps API response codes to a tri-state:
 *
 *   - **HTTP 200** → `hasAccess: true` (the signed-in user owns the
 *     conversation; the public deal page should redirect them to the
 *     authenticated builder so they get the full session).
 *   - **HTTP 403 / 404** → `hasAccess: false` (RLS deny OR not found —
 *     do NOT 403; recipient may be signed in under a different account
 *     from the deal owner; stay on the public chrome with the gate).
 *   - **HTTP 410 (`MODEL_VERSION_REMOVED`)** → `hasAccess: true` (the
 *     authenticated user can recover via the existing fork flow; this
 *     hook's job is "is this conversation reachable for them," not
 *     "is the model still serviceable").
 *   - **Loading** → `hasAccess: undefined` (consumer must gate the
 *     redirect side-effect on `isLoading === false`).
 *
 * Wraps the existing `useConversation` query key so the cache hit
 * survives the redirect (the authenticated builder reads the same key
 * on mount).
 *
 * `enabled` is the caller's responsibility: pass `null` for the
 * conversation id when the user isn't signed in (skip the probe
 * entirely — anonymous viewers stay on the public surface by
 * construction).
 */

export interface ConversationAccessState {
  hasAccess: boolean | undefined;
  isLoading: boolean;
}

export function useConversationAccess(
  conversationId: string | null,
): ConversationAccessState {
  const query = useQuery({
    queryKey: conversationId
      ? queryKeys.conversations.detail(conversationId)
      : ['conversation-access', 'none'],
    queryFn: async () => {
      if (!conversationId) throw new Error('conversationId required');
      // We only need the access verdict — the full payload is fetched by
      // the authenticated builder when the redirect lands. A bare GET is
      // cheaper than the dual-endpoint shape `useConversation` does, but
      // we share the query key so the next consumer's `useConversation`
      // hits the cache.
      return api.get(`/api/conversations/${conversationId}`);
    },
    enabled: !!conversationId,
    retry: (failureCount, error) => {
      // Don't retry on 403/404 — those are deterministic access verdicts.
      if (error instanceof ApiError) {
        if (error.status === 403 || error.status === 404) return false;
      }
      return failureCount < 1;
    },
    // Soft cache so the answer survives a brief unmount/remount (e.g.
    // route transition through the redirect effect). The auth-builder
    // can immediately render against the cached payload.
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return { hasAccess: undefined, isLoading: true };
  }

  if (query.error instanceof MissingModelError) {
    // 410 — the user has access; the model just isn't serviceable.
    // Redirect to builder; the builder's MissingModelPanel handles it.
    return { hasAccess: true, isLoading: false };
  }

  if (query.error instanceof ApiError) {
    if (query.error.status === 403 || query.error.status === 404) {
      return { hasAccess: false, isLoading: false };
    }
    // Other 5xx → treat as no-access for redirect purposes (don't
    // navigate to builder when the API is down; the public chrome is
    // safer until the page recovers).
    return { hasAccess: false, isLoading: false };
  }

  if (query.error) {
    return { hasAccess: false, isLoading: false };
  }

  if (query.data) {
    return { hasAccess: true, isLoading: false };
  }

  return { hasAccess: undefined, isLoading: false };
}
